/**
 * Who created / shared in each board of a team, as the server knows it
 * (`created_by` of GET /teams/:id/boards, backend_pheonix
 * Sync.TeamBoardOwners). Only asked for teams that have a board without
 * `createdBy` (made before 1.0.7): at most one request per team per session,
 * cached in `boardsUiStore.teamBoardOwners`.
 *
 * A server without the field (before the backend deploy) answers boards with
 * no `created_by`: the map stays empty, nothing is filled in, no owner shows.
 */
import teamsService from '../../../services/teamsService';
import { useBoardsUiStore } from './boardsUiStore';
import { ownersFromTeamBoards } from './boardCreatedBy';

const inFlight = new Map();

/** The cached map for a team, or undefined when it was never loaded. */
export function cachedTeamBoardOwners(teamId) {
  return useBoardsUiStore.getState().teamBoardOwners[String(teamId)];
}

/**
 * Load (once) and cache the owners of a team's boards. Resolves to the map;
 * a failed request resolves to `{}` and is NOT cached, so a later board open
 * may try again.
 */
export function loadTeamBoardOwners(teamId) {
  if (teamId == null) return Promise.resolve({});
  const key = String(teamId);
  const cached = cachedTeamBoardOwners(key);
  if (cached) return Promise.resolve(cached);
  if (inFlight.has(key)) return inFlight.get(key);
  const request = teamsService
    .getTeamBoards(key)
    .then((list) => {
      const owners = ownersFromTeamBoards(Array.isArray(list) ? list : list?.data);
      useBoardsUiStore.getState().setTeamBoardOwners(key, owners);
      return owners;
    })
    .catch(() => ({}))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
}
